import { Button, Checkbox, InfoIcon, Input, Modal } from "@bp/ui-components";
import { useState } from "react";
import { useMutation } from "urql";
import { graphql } from "../../../client/gql";
import { InfoBox } from "../../../components/InfoBox/InfoBox";
import { showSuccessToast } from "../../../utils/toast";
import css from "../orgList.module.scss";

const GroupModalCreateGroup = graphql(`
  mutation GroupModal_CreateGroup($input: [GroupCreateInput!]!) {
    createGroups(input: $input) {
      groups {
        name
      }
    }
  }
`);

const GroupModalUpdateGroup = graphql(`
  mutation GroupModal_UpdateGroup($where: GroupWhere, $update: GroupUpdateInput) {
    updateGroups(where: $where, update: $update) {
      groups {
        uuid
      }
    }
  }
`);

type Props = {
  org: { uuid: string; name: string };
  /** When set, the modal edits this group (rename + visibility) instead of creating one. */
  group?: { uuid: string; name: string; publicVisible?: boolean | null };
  onClose: () => void;
  onSaved: () => void;
};

export function GroupModal({ org, group, onClose, onSaved }: Props) {
  const isEdit = Boolean(group);
  const [name, setName] = useState(group?.name ?? "");
  const [publicVisible, setPublicVisible] = useState(Boolean(group?.publicVisible));
  const [, createGroup] = useMutation(GroupModalCreateGroup);
  const [, updateGroup] = useMutation(GroupModalUpdateGroup);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }
    if (group) {
      await updateGroup({
        where: { uuid: group.uuid },
        update: { name: trimmed, publicVisible },
      });
      showSuccessToast(`Gruppe „${trimmed}" gespeichert`);
    } else {
      await createGroup({
        input: [
          {
            name: trimmed,
            publicVisible,
            organization: { connect: { where: { node: { uuid: org.uuid } } } },
          },
        ],
      });
      showSuccessToast(`Gruppe „${trimmed}" angelegt`);
    }
    onSaved();
  };

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      hideFooter={false}
      width="s"
      title={isEdit ? "Gruppe bearbeiten" : `Gruppe anlegen — ${org.name}`}
      onRequestClose={onClose}
      footer={
        <div className={css.foot}>
          <span className={css.footHint}>Gilt in {org.name}.</span>
          <div className={css.footActions}>
            <Button hierarchy="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button hierarchy="primary" disabled={!name.trim()} onClick={() => void submit()}>
              {isEdit ? "Speichern" : "Anlegen"}
            </Button>
          </div>
        </div>
      }
    >
      <div className={css.stack}>
        <Input
          name="groupName"
          label="Name"
          value={name}
          autoFocus
          placeholder="z. B. Kollegium, Klasse 7b"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              void submit();
            }
          }}
        />
        <div className={css.toggleRow}>
          <Checkbox
            name="publicVisible"
            checked={publicVisible}
            onChange={(e) => setPublicVisible(e.target.checked)}
          />
          <span>
            <span className={css.toggleRowTitle}>Öffentlich sichtbar</span>
            <span className={css.toggleRowSub}>
              Gruppe ist für Mitglieder der Organisation auffindbar.
            </span>
          </span>
        </div>
        <InfoBox tone="primary" icon={<InfoIcon style={{ fontSize: 15 }} />}>
          <span style={{ fontSize: 12 }}>
            Mitglieder lassen sich nach dem Anlegen über „Mitglieder bearbeiten“ zuordnen.
          </span>
        </InfoBox>
      </div>
    </Modal>
  );
}
